import { SxProps, Typography } from '@mui/material';

import Heading from '@/common/components/RichText/components/Heading/Heading';
import Hyperlink from '@/common/components/RichText/components/Hyperlink/Hyperlink';
import InternalLink from '@/common/components/RichText/components/InternalLink/InternalLink';
import { ParagraphStyles } from './Paragraph.styles';
import { RichTextNode } from '@/common/components/RichText/RichTextService';

interface ParagraphProps {
  node: RichTextNode;
  sx?: SxProps;
}

export default function Paragraph({ node, sx }: ParagraphProps) {
  if (!node.content || node.content.length === 0) return null;

  const renderTextWithMarks = (
    text: string | undefined,
    marks: { type: string }[],
    index: number
  ) => {
    if (!text) return null;
    let formattedText = <span key={`${index}-text`}>{text}</span>;

    marks.forEach((mark) => {
      switch (mark.type) {
        case 'bold':
          formattedText = (
            <strong key={`${index}-bold`}>{formattedText}</strong>
          );
          break;
        case 'italic':
          formattedText = <em key={`${index}-italic`}>{formattedText}</em>;
          break;
        case 'underline':
          formattedText = <u key={`${index}-underline`}>{formattedText}</u>;
          break;
        default:
          break;
      }
    });

    return formattedText;
  };

  return (
    <Typography
      variant="body1"
      component="div"
      className="paragraph-text"
      sx={{
        ...ParagraphStyles(sx),
        '& > *': {
          display: 'inline',
        },
      }}
    >
      {node.content.map((childNode, index) => {
        if (childNode.value?.length === 0) {
          return null;
        }

        if (childNode.nodeType === 'text') {
          return renderTextWithMarks(
            childNode.value,
            childNode.marks || [],
            index
          );
        }

        if (childNode.nodeType === 'hyperlink') {
          return <Hyperlink key={index} node={childNode} />;
        }

        if (childNode.nodeType === 'entry-hyperlink') {
          return <InternalLink key={index} node={childNode} />;
        }

        if (childNode.nodeType.startsWith('heading-')) {
          return <Heading key={index} node={childNode} />;
        }

        return null;
      })}
    </Typography>
  );
}
